import { useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CalendarDays, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { api } from "@/lib/api";

function fmt(d) {
  if (!d) return "";
  return format(d, "EEE, MMM d, yyyy");
}

export default function InquiryForm() {
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState("2");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    const ms = range.to.getTime() - range.from.getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }, [range]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!range.from || !range.to) {
      toast.error("Please select your check-in and check-out dates.");
      return;
    }
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your name and email so we can reply.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        checkin: format(range.from, "yyyy-MM-dd"),
        checkout: format(range.to, "yyyy-MM-dd"),
        guests: parseInt(guests, 10),
        message: message.trim() || null,
      });
      toast.success("Your inquiry has been sent. We'll reply within 24 hours.");
      setRange({ from: undefined, to: undefined });
      setGuests("2");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not send. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquire"
      data-testid="inquiry-section"
      className="py-20 md:py-28 bg-[#EAE0D5]/60"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="overline">Inquire</p>
            <h2
              data-testid="inquiry-title"
              className="font-serif-display text-4xl md:text-5xl mt-3 leading-[1.1] text-[#232E26]"
            >
              Tell us when you&apos;d like to come<br />
              <em className="not-italic text-[#C65D47]">&mdash; we&apos;ll handle the rest.</em>
            </h2>
            <p className="mt-6 text-[#232E26]/80 leading-relaxed max-w-md">
              Share your dates and a short note. We reply personally within 24
              hours with availability, the total for your stay and any local
              recommendations you ask for.
            </p>
            <div className="mt-8 space-y-3 text-[#5B6E4A] text-sm">
              <p>· Minimum stay: 3 nights (peak season).</p>
              <p>· Sleeps up to 4 guests comfortably.</p>
              <p>· No booking fees · pay your hosts directly.</p>
            </div>
          </div>

          <form
            data-testid="inquiry-form"
            onSubmit={onSubmit}
            className="md:col-span-7 bg-[#FAF7F2] p-6 md:p-10 shadow-soft border border-[#EAE0D5]"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <Label className="overline" htmlFor="dates-trigger">
                  Dates
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      id="dates-trigger"
                      type="button"
                      data-testid="dates-trigger"
                      className="mt-2 w-full h-12 px-4 border border-[#5B6E4A]/25 bg-white text-left text-[#232E26] flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#C65D47]"
                    >
                      <span className="flex items-center gap-3">
                        <CalendarDays size={18} className="text-[#C65D47]" />
                        {range.from && range.to ? (
                          <span>
                            {fmt(range.from)} — {fmt(range.to)}
                          </span>
                        ) : range.from ? (
                          <span>
                            {fmt(range.from)} — pick check-out
                          </span>
                        ) : (
                          <span className="text-[#5B6E4A]">
                            Select check-in & check-out
                          </span>
                        )}
                      </span>
                      {nights > 0 && (
                        <span className="text-xs tracking-[0.2em] uppercase text-[#5B6E4A]">
                          {nights} night{nights === 1 ? "" : "s"}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="p-0 bg-white border-[#EAE0D5]"
                  >
                    <Calendar
                      mode="range"
                      numberOfMonths={2}
                      selected={range}
                      onSelect={(v) => setRange(v || { from: undefined, to: undefined })}
                      disabled={{ before: today }}
                      data-testid="date-range-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="overline" htmlFor="guests">
                  Guests
                </Label>
                <Select value={guests} onValueChange={setGuests}>
                  <SelectTrigger
                    id="guests"
                    data-testid="guests-select"
                    className="mt-2 h-12 bg-white border-[#5B6E4A]/25 focus:ring-[#C65D47]"
                  >
                    <SelectValue placeholder="Guests" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {[1, 2, 3, 4].map((n) => (
                      <SelectItem
                        key={n}
                        value={String(n)}
                        data-testid={`guests-${n}`}
                      >
                        {n} guest{n > 1 ? "s" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="overline" htmlFor="phone">
                  Phone (optional)
                </Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  type="tel"
                  placeholder="+30 …"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-2 h-12 bg-white border-[#5B6E4A]/25 focus-visible:ring-[#C65D47]"
                />
              </div>

              <div>
                <Label className="overline" htmlFor="name">
                  Your name
                </Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Maria Papadopoulos"
                  className="mt-2 h-12 bg-white border-[#5B6E4A]/25 focus-visible:ring-[#C65D47]"
                />
              </div>

              <div>
                <Label className="overline" htmlFor="email">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2 h-12 bg-white border-[#5B6E4A]/25 focus-visible:ring-[#C65D47]"
                />
              </div>

              <div className="sm:col-span-2">
                <Label className="overline" htmlFor="message">
                  Anything you&apos;d like us to know?
                </Label>
                <Textarea
                  id="message"
                  data-testid="message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  placeholder="Travelling with kids? Looking for quiet beaches? Tell us how we can make your stay perfect."
                  className="mt-2 bg-white border-[#5B6E4A]/25 focus-visible:ring-[#C65D47]"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="inquiry-submit"
              disabled={submitting}
              className="btn-terra mt-8 w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm text-sm tracking-wide uppercase disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send inquiry <Send size={16} />
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-[#5B6E4A]">
              Your details stay with the hosts. We never share them.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
